import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useDispatch } from "react-redux";
import { userLoaded, authProviderUnavailable, stopWatchingAuthedUserAction } from "../../features/auth/authSlice";
import { ClerkAuthProvider } from './clerk-auth';
import logging from "utils/logging";

const log = logging.getLogger("AuthProvider");
log.setLevel('info');

// Define the auth provider interface
export class AuthProviderInterface {
  constructor() {
    if (this.constructor === AuthProviderInterface) {
      throw new Error("Cannot instantiate abstract class");
    }
  }

  async getCurrentUser() {
    throw new Error("Method 'getCurrentUser' must be implemented");
  }

  async getAnonymousUser() {
    throw new Error("Method 'getAnonymousUser' must be implemented");
  }

  async watchAuthedUser(callback) {
    throw new Error("Method 'watchAuthedUser' must be implemented");
  }

  async logOut() {
    throw new Error("Method 'logOut' must be implemented");
  }

  getProviderComponent() {
    throw new Error("Method 'getProviderComponent' must be implemented");
  }
}

// Create auth context
const AuthContext = createContext();

// Base AuthProvider component that works with any auth provider implementation
const BaseAuthProvider = ({ children, provider }) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!provider) {
      console.error('No auth provider provided to AuthProvider');
      setLoading(false);
      return;
    }

    let cleanup = null;
    let cancelled = false;

    // Fallback: if the provider neither resolves nor rejects (e.g. Clerk's
    // load() hangs because a mobile browser blocks its cross-domain storage),
    // stop gating the UI so public routes still render unauthenticated.
    const loadTimeout = setTimeout(() => {
      if (!cancelled) {
        log.warn("Auth provider slow to initialize; rendering unauthenticated");
        setLoading(false);
        dispatch(authProviderUnavailable());
      }
    }, 5000);

    // Set up the auth state listener. watchAuthedUser may return an unsubscribe
    // function synchronously, or a promise that resolves to one (when the
    // provider has to initialize first). Normalize both with Promise.resolve.
    Promise.resolve(
      provider.watchAuthedUser((newUser) => {
        // Only update if the user has actually changed
        const currentUserId = user?.uid;
        const newUserId = newUser?.uid;

        if (currentUserId !== newUserId) {
          log.debug("User changed, updating state", user, "-->", newUser);
          setUser(newUser);
          setLoading(false);
          // Dispatch Redux action when auth state changes
          dispatch(userLoaded({ user: newUser }));
        } else {
          if (newUser === null) {
            setUser(null);
            setLoading(false);
            dispatch(userLoaded({ user: null }));
          }
          log.debug("User unchanged, skipping update");
        }
      })
    )
      .then((unsubscribe) => {
        if (cancelled) {
          if (typeof unsubscribe === "function") unsubscribe();
          return;
        }
        clearTimeout(loadTimeout);
        cleanup = typeof unsubscribe === "function" ? unsubscribe : null;
      })
      .catch((error) => {
        clearTimeout(loadTimeout);
        // The auth provider failed to initialize — e.g. Clerk refuses to load
        // on an unregistered domain (PR preview channels). Don't hang on the
        // loading gate: render the app unauthenticated so public routes (home,
        // 404) still work. Protected routes fall back to their own redirects.
        log.error("Auth provider failed to initialize; continuing unauthenticated", error);
        setUser(null);
        setLoading(false);
        dispatch(authProviderUnavailable());
      });

    // Cleanup function
    return () => {
      cancelled = true;
      clearTimeout(loadTimeout);
      if (typeof cleanup === "function") {
        cleanup();
      }
      dispatch(stopWatchingAuthedUserAction());
    };
  }, [provider, user, dispatch]);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    provider,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Factory function to create the appropriate AuthProvider
const createAuthProvider = (providerType = 'clerk', options = {}) => {
  switch (providerType.toLowerCase()) {
    case 'clerk':
      return ClerkAuthProvider.getInstance();
    // Add other provider types here
    default:
      throw new Error(`Unsupported auth provider type: ${providerType}`);
  }
};

// The main AuthProvider component that wraps the application
export const AuthProviderComponent = ({ children }) => {
  const provider = createAuthProvider('clerk');
  // getProviderComponent() returns a fresh component each call; memoize on the
  // (singleton) provider so its identity is stable and the ClerkProvider isn't
  // remounted — and its Clerk session re-initialized — on every render.
  const ProviderComponent = useMemo(() => provider.getProviderComponent(), [provider]);

  return (
    <ProviderComponent>
      <BaseAuthProvider provider={provider}>
        {children}
      </BaseAuthProvider>
    </ProviderComponent>
  );
}; 

export const AuthProvider = () => {
  const provider = createAuthProvider('clerk');
  return provider;
}